/**
 * AddStepDialog Component
 * Dialog for adding a new step to a schedule.
 * Two-step flow: 1) Select action/schedule/board, 2) Configure step details.
 */
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  TextField,
  FormControlLabel,
  Switch,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  StepCreate,
  StepType,
  CompletionAction,
  TimerStyle,
  Schedule,
  ScheduleType,
  DayOfWeek,
  SCHEDULE_VALIDATION,
} from '../../types/schedule';
import { Action } from '../../types/action';
import { scheduleService } from '../../services/scheduleService';
import { ActionsTab } from './AddStepTabs/ActionsTab';
import { SchedulesTab } from './AddStepTabs/SchedulesTab';
import { BoardsTab } from './AddStepTabs/BoardsTab';

interface AddStepDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  deviceId: string;
  scheduleId: string;
  currentScheduleId: string; // For filtering out current schedule
  deviceActions: Action[];
  actionsError?: boolean; // The actions could not be loaded (BA-145)
  deviceSchedules: Schedule[];
  defaultOrder?: number; // For First-Then boards to specify position
  onActionsChange?: () => void; // Callback when new action is created
  onSchedulesChange?: () => void; // Callback when new schedule is created
  scheduleType?: ScheduleType; // The board being added to: a choice board says "choice", not "step" (BA-124)
  day?: DayOfWeek; // Optional: for weekly schedules, specifies the day to add step to
}

type TabValue = 'actions' | 'schedules' | 'boards';
type DialogStep = 'select' | 'configure';

// Schedule types for each tab
const SCHEDULE_TAB_TYPES: ScheduleType[] = ['routine', 'daily_schedule'];
const BOARD_TAB_TYPES: ScheduleType[] = ['first_then', 'choice_board', 'waiting_board'];

interface SelectedItem {
  type: StepType;
  action?: Action;
  schedule?: Schedule;
}

export const AddStepDialog: React.FC<AddStepDialogProps> = ({
  open,
  onClose,
  onAdded,
  deviceId,
  scheduleId,
  currentScheduleId,
  deviceActions,
  actionsError,
  day,
  deviceSchedules,
  defaultOrder,
  onActionsChange,
  onSchedulesChange,
  scheduleType,
}) => {
  const { t } = useTranslation();
  const isChoiceBoard = scheduleType === 'choice_board';

  // Dialog flow state
  const [dialogStep, setDialogStep] = useState<DialogStep>('select');
  const [activeTab, setActiveTab] = useState<TabValue>('actions');
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);

  // Step configuration state
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [enableTimer, setEnableTimer] = useState(false);
  const [timerDuration, setTimerDuration] = useState(60);
  const [timerWarningSeconds, setTimerWarningSeconds] = useState<number | undefined>(undefined);
  const [timerStyle, setTimerStyle] = useState<TimerStyle>('shrinking_disk');
  const [completionAction, setCompletionAction] = useState<CompletionAction>('tap');
  const [isSkippable, setIsSkippable] = useState(true);
  const [tokenValue, setTokenValue] = useState(0);
  const [helpPrompt, setHelpPrompt] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset all state
  const resetState = useCallback(() => {
    setDialogStep('select');
    setActiveTab('actions');
    setSelectedItem(null);
    setTitle('');
    setSubtitle('');
    setEnableTimer(false);
    setTimerDuration(60);
    setTimerWarningSeconds(undefined);
    setTimerStyle('shrinking_disk');
    setCompletionAction('tap');
    setIsSkippable(true);
    setTokenValue(0);
    setHelpPrompt('');
    setError(null);
  }, []);

  const handleClose = () => {
    if (!saving) {
      resetState();
      onClose();
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };

  const handleSelectAction = (action: Action) => {
    setSelectedItem({ type: 'action', action });
    setTitle(action.name);
    setDialogStep('configure');
  };

  const handleSelectSchedule = (schedule: Schedule) => {
    setSelectedItem({ type: 'schedule', schedule });
    setTitle(schedule.name);
    setDialogStep('configure');
  };

  const handleBack = () => {
    setDialogStep('select');
    setSelectedItem(null);
    setTitle('');
    setSubtitle('');
    setError(null);
  };

  const handleSave = async () => {
    if (!selectedItem || !title.trim()) return;

    setSaving(true);
    setError(null);

    try {
      const timerData = enableTimer
        ? {
            durationSeconds: timerDuration,
            warningSeconds: timerWarningSeconds,
            style: timerStyle,
          }
        : undefined;

      const stepData: StepCreate = {
        title: title.trim(),
        subtitle: subtitle.trim() || undefined,
        stepType: selectedItem.type,
        actionId: selectedItem.type === 'action' ? selectedItem.action?.id : undefined,
        scheduleId: selectedItem.type === 'schedule' ? selectedItem.schedule?.id : undefined,
        timer: timerData,
        completionAction,
        isSkippable,
        tokenValue,
        helpPrompt: helpPrompt.trim() || undefined,
      };

      // Use day-specific endpoint for weekly schedules, regular endpoint otherwise
      if (day) {
        await scheduleService.addDayStep(deviceId, scheduleId, day, stepData, defaultOrder);
      } else {
        await scheduleService.addStep(deviceId, scheduleId, stepData, defaultOrder);
      }

      handleClose();
      onAdded();
    } catch (err: any) {
      console.error('Failed to add step:', err);
      if (err?.message?.includes('circular')) {
        setError(t('schedules.errors.circularReference'));
      } else {
        setError(t(isChoiceBoard ? 'schedules.choiceBoard.addDialog.failed' : 'schedules.errors.failedToAddStep'));
      }
    } finally {
      setSaving(false);
    }
  };

  const isValid = title.trim().length > 0 && selectedItem !== null;

  // Filter schedules for each tab
  const scheduleTabSchedules = deviceSchedules.filter(
    (s) => SCHEDULE_TAB_TYPES.includes(s.type) && s.id !== currentScheduleId && s.isActive
  );
  const boardTabSchedules = deviceSchedules.filter(
    (s) => BOARD_TAB_TYPES.includes(s.type) && s.id !== currentScheduleId && s.isActive
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {dialogStep === 'configure' && (
          <IconButton onClick={handleBack} size="small" disabled={saving}>
            <BackIcon />
          </IconButton>
        )}
        {dialogStep === 'select'
          ? t(isChoiceBoard ? 'schedules.choiceBoard.addDialog.title' : 'schedules.addStep.title')
          : t(isChoiceBoard ? 'schedules.choiceBoard.addDialog.configure' : 'schedules.addStep.configureStep')}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dialogStep === 'select' ? (
          <>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label={t('schedules.addStep.tabs.actions')} value="actions" />
                <Tab label={t('schedules.addStep.tabs.schedules')} value="schedules" />
                <Tab label={t('schedules.addStep.tabs.boards')} value="boards" />
              </Tabs>
            </Box>

            {/* Tab content */}
            {activeTab === 'actions' && (
              <ActionsTab
                actions={deviceActions}
                error={actionsError}
                deviceId={deviceId}
                onSelect={handleSelectAction}
                onActionCreated={onActionsChange}
              />
            )}
            {activeTab === 'schedules' && (
              <SchedulesTab
                schedules={scheduleTabSchedules}
                deviceId={deviceId}
                onSelect={handleSelectSchedule}
                onScheduleCreated={onSchedulesChange}
                allowedTypes={SCHEDULE_TAB_TYPES}
              />
            )}
            {activeTab === 'boards' && (
              <BoardsTab
                schedules={boardTabSchedules}
                deviceId={deviceId}
                onSelect={handleSelectSchedule}
                onScheduleCreated={onSchedulesChange}
                allowedTypes={BOARD_TAB_TYPES}
              />
            )}
          </>
        ) : (
          /* Step configuration form */
          <Box>
            {/* Selected item preview */}
            {selectedItem && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  mb: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                }}
              >
                {selectedItem.type === 'action' && selectedItem.action && (
                  <>
                    {selectedItem.action.thumbnailUrl || selectedItem.action.pictureUrl ? (
                      <Box
                        component="img"
                        src={selectedItem.action.thumbnailUrl || selectedItem.action.pictureUrl || ''}
                        alt={selectedItem.action.name}
                        sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: 'grey.200',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography color="text.secondary">?</Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="subtitle2">{selectedItem.action.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('schedules.addStep.actionSelected')}
                      </Typography>
                    </Box>
                  </>
                )}
                {selectedItem.type === 'schedule' && selectedItem.schedule && (
                  <>
                    <Box sx={{ fontSize: '2rem' }}>
                      {selectedItem.schedule.icon?.emoji || '📋'}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">{selectedItem.schedule.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t(`schedules.types.${selectedItem.schedule.type}`)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* Title */}
            <TextField
              fullWidth
              label={t(isChoiceBoard ? 'schedules.choiceBoard.addDialog.choiceTitle' : 'schedules.steps.stepTitle')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: SCHEDULE_VALIDATION.STEP_TITLE_MAX_LENGTH }}
              disabled={saving}
              autoFocus
            />

            {/* Subtitle */}
            <TextField
              fullWidth
              label={t('schedules.steps.stepSubtitle')}
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: SCHEDULE_VALIDATION.STEP_SUBTITLE_MAX_LENGTH }}
              disabled={saving}
            />

            {/* Timer toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={enableTimer}
                  onChange={(e) => setEnableTimer(e.target.checked)}
                  disabled={saving}
                />
              }
              label={t('schedules.steps.timer')}
              sx={{ mb: 1 }}
            />

            {/* Timer configuration */}
            {enableTimer && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" gutterBottom>
                  {t('schedules.steps.timerDuration')}: {timerDuration}s
                </Typography>
                <Slider
                  value={timerDuration}
                  onChange={(_, value) => setTimerDuration(value as number)}
                  min={SCHEDULE_VALIDATION.TIMER_MIN_SECONDS}
                  max={600}
                  step={5}
                  marks={[
                    { value: 30, label: '30s' },
                    { value: 60, label: '1m' },
                    { value: 120, label: '2m' },
                    { value: 300, label: '5m' },
                    { value: 600, label: '10m' },
                  ]}
                  disabled={saving}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  type="number"
                  label={t('schedules.steps.timerWarning')}
                  value={timerWarningSeconds || ''}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setTimerWarningSeconds(val > 0 ? val : undefined);
                  }}
                  helperText={t('schedules.steps.timerWarningHelp')}
                  inputProps={{ min: 5, max: timerDuration - 5 }}
                  disabled={saving}
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth>
                  <InputLabel>{t('schedules.steps.timerStyle')}</InputLabel>
                  <Select
                    value={timerStyle}
                    onChange={(e) => setTimerStyle(e.target.value as TimerStyle)}
                    label={t('schedules.steps.timerStyle')}
                    disabled={saving}
                  >
                    <MenuItem value="shrinking_disk">{t('schedules.timerStyles.shrinking_disk')}</MenuItem>
                    <MenuItem value="shrinking_bar">{t('schedules.timerStyles.shrinking_bar')}</MenuItem>
                    <MenuItem value="countdown">{t('schedules.timerStyles.countdown')}</MenuItem>
                    <MenuItem value="hourglass">{t('schedules.timerStyles.hourglass')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Completion action */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('schedules.steps.completionAction')}</InputLabel>
              <Select
                value={completionAction}
                onChange={(e) => setCompletionAction(e.target.value as CompletionAction)}
                label={t('schedules.steps.completionAction')}
                disabled={saving}
              >
                <MenuItem value="tap">{t('schedules.completionActions.tap')}</MenuItem>
                <MenuItem value="swipe">{t('schedules.completionActions.swipe')}</MenuItem>
                <MenuItem value="hold">{t('schedules.completionActions.hold')}</MenuItem>
                <MenuItem value="auto">{t('schedules.completionActions.auto')}</MenuItem>
                <MenuItem value="parent_only">{t('schedules.completionActions.parent_only')}</MenuItem>
              </Select>
            </FormControl>

            {/* Skippable toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={isSkippable}
                  onChange={(e) => setIsSkippable(e.target.checked)}
                  disabled={saving}
                />
              }
              label={t('schedules.steps.skippable')}
              sx={{ mb: 2 }}
            />

            {/* Token value */}
            <TextField
              fullWidth
              type="number"
              label={t('schedules.steps.tokenValue')}
              value={tokenValue}
              onChange={(e) =>
                setTokenValue(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))
              }
              inputProps={{
                min: SCHEDULE_VALIDATION.TOKEN_MIN_VALUE,
                max: SCHEDULE_VALIDATION.TOKEN_MAX_VALUE,
              }}
              disabled={saving}
              sx={{ mb: 2 }}
            />

            {/* Help prompt */}
            <TextField
              fullWidth
              label={t('schedules.steps.helpPrompt')}
              value={helpPrompt}
              onChange={(e) => setHelpPrompt(e.target.value)}
              multiline
              rows={2}
              helperText={t('schedules.steps.helpPromptHelp')}
              disabled={saving}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        {dialogStep === 'configure' && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isValid || saving}
            startIcon={saving && <CircularProgress size={16} />}
          >
            {saving ? t('common.saving') : t('common.add')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
